import { API_BASE_URL } from "@/config";

/**
 * Client-specific search permission overrides
 * When a client has a custom sidebar, restrict their search to relevant categories only
 */
export const CLIENT_SEARCH_OVERRIDES = {
  'suzuki': {
    'Client': ['users', 'assets', 'services']
  },
  'swift': {
    'Client': ['users', 'assets', 'services']
  },
  'mobiafrica': {
    'Client': ['users', 'assets', 'services']
  },
  'verify': {
    'Client': ['users', 'assets', 'services']
  }
};

/**
 * Detect client name from current domain
 */
export function getClientNameFromDomain() {
  const domain = window.location.hostname;
  const clientMappings = [
    'verify', 'mobiafrica', 'suzuki', 'autocity', 'yumbi', 
    'santam', 'altron', 'swift', 'webuycars', 'getsavvi'
  ];

  for (const client of clientMappings) {
    if (domain.includes(client)) {
      return client;
    }
  }
  return null;
}

export async function global_search(query) {
  const TOKEN = localStorage.getItem('token');

  if (!query || query.trim().length === 0) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/verify_api/system_management_api/global_search_api/?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: {
        Authorization: `Token ${TOKEN}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch search results");
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error("Global Search Error:", error);
    return null;
  }
}

export default {
  data: () => ({
    global_search_query: "",
    global_search_results: null,
    show_results: false,
    search_timer: null,
    detected_client_name: null,
  }),
  created() {
    this.detected_client_name = getClientNameFromDomain();
  },
  computed: {
    user_current_role() {
      return this.global_search_results?.current_user_role || '';
    },
    
    role_permissions() {
      return {
        SystemAdmin: ['users', 'workflows', 'services', 'assets'],
        Client: ['users', 'workflows', 'services', 'projects', 'assets', 'templates', 'questions'],
        ClientAdmin: ['users', 'workflows', 'services', 'projects', 'assets'],
        Customer: ['workflows']
      };
    },

    viewable_user_roles() {
      return {
        SystemAdmin: ['Customer', 'Client', 'ClientAdmin', 'SystemAdmin'],
        Client: ['Customer', 'ClientAdmin'],
        ClientAdmin: ['Customer'],
        Customer: []
      };
    },
    
    allowed_categories() {
      const role = this.user_current_role;
      const clientName = this.detected_client_name;

      let categories = [];    

      if (clientName && CLIENT_SEARCH_OVERRIDES[clientName] && CLIENT_SEARCH_OVERRIDES[clientName][role]) {
        categories = [...CLIENT_SEARCH_OVERRIDES[clientName][role]];
      } else {
        categories = [...(this.role_permissions[role] || [])];
      }

      if (!this.can_view_workflows) {
        categories = categories.filter(cat => cat !== 'workflows');
      }

      return categories;
    },

    is_workbench_client() {
      const workbench_clients = ['suzuki', 'swift', 'mobiafrica', 'verify'];
      return workbench_clients.includes(this.detected_client_name);
    },

    filtered_search_results() {
      if (!this.global_search_results) return null;
      
      const filtered = {};
      const allowed = this.allowed_categories;
      
      for (const [category, data] of Object.entries(this.global_search_results)) {
        if (category === 'current_user_role') {
          filtered[category] = data;
          continue;
        }
        
        if (!allowed.includes(category)) {
          continue;
        }
        
        if (category === 'users' && Array.isArray(data)) {
          const viewable_roles = this.viewable_user_roles[this.user_current_role] || [];
          const filtered_users = data.filter(user => viewable_roles.includes(user.user_role));
          if (filtered_users.length > 0) {
            filtered[category] = filtered_users;
          }
        } else {
          filtered[category] = data;
        }
      }
      
      return filtered;
    },
    
    has_results() {
      if (!this.filtered_search_results) return false;
      return Object.entries(this.filtered_search_results).some(
        ([key, v]) => key !== 'current_user_role' && Array.isArray(v) && v.length > 0
      );
    },
    can_view_workflows() {
      const role = this.user_current_role;

      if (role === 'SystemAdmin' || role === 'ClientAdmin') {
        return true;
      }

      if (role === 'Client' && !this.is_workbench_client) {
        return true;
      }

      return false;
    },
  },
  methods: {
    handle_global_search() {
      clearTimeout(this.search_timer);
      if (this.global_search_query.trim() === "") {
        this.global_search_results = null;
        this.show_results = false;
        return;
      }

      this.search_timer = setTimeout(async () => {
        this.global_search_results = await global_search(this.global_search_query);
        this.show_results = true;
      }, 300);
    },

    is_category_allowed(category) {
      return this.allowed_categories.includes(category);
    },
    
    get_filtered_users_by_role(role) {
      if (!this.filtered_search_results?.users) return [];
      return this.filtered_search_results.users.filter(u => u.user_role === role);
    },
    
    is_user_role_viewable(user_role) {
      const viewable = this.viewable_user_roles[this.user_current_role] || [];
      return viewable.includes(user_role);
    },

    handle_search_focus() {
      if (this.global_search_query.trim() && this.global_search_results) {
        this.show_results = true;
      } else if (this.global_search_query.trim()) {
        this.handle_global_search();
      }
    },
    
    clear_search() {
      this.global_search_query = "";
      this.global_search_results = null;
      this.show_results = false;
    },
    
    go_to_service(service_id, service_type) {
      const role = this.user_current_role;

      if (service_type === 'system generated') {
        this.$store.commit('set_service_id', service_id);
        this.$router.push({ name: 'manage_form' });
      } else if (role === 'SystemAdmin') {
        this.$router.push({ name: 'service_util_mangement', params: { service_id } });
      } else if (role === 'Client' && this.is_workbench_client) {
        this.$router.push({ name: 'utilize_service', params: { service_id } });
      } else {
        this.$router.push({ name: 'view_service', params: { id: service_id } });
      }
      this.clear_search();
    },
    
    go_to_project(project_id) {
      this.$store.commit('set_project_id', project_id);
      this.$router.push({ name: 'project_dashboard' });
      this.clear_search();
    },

    view_customers(workflow_id) {
      const role = this.user_current_role;
      
      if (role === 'SystemAdmin') {
        this.$router.push({ name: 'view_workflow_clients', params: { workflow_id: workflow_id } });
      } else {
        this.$store.commit('set_workflow_id', workflow_id);
        this.$router.push({ name: 'client_view_workflow' });
      }
      this.clear_search();
    },

    edit_workflow(workflow_id) {
      const role = this.user_current_role;
      
      if (role === 'SystemAdmin') {
        this.$router.push({ name: 'view_single_workflow', params: { workflow_id: workflow_id } });
      } else {
        this.$store.commit('set_workflow_id', workflow_id);
        this.$router.push({ name: 'edit_workflow' });
      }
      this.clear_search();
    },

    go_to_asset(asset_id) {
      const role = this.user_current_role;

      if (role === 'SystemAdmin') {
        this.$router.push({ name: 'view_single_asset', params: { id: asset_id } });
      } else if (role === 'Client' && this.is_workbench_client) {
        this.$router.push({ name: 'toolbox' });
      } else {
        this.$router.push({ name: 'view_asset', params: { id: asset_id } });
      }
      this.clear_search();
    },
    
    go_to_template(template_id) {
      this.$router.push({ name: 'template_configuration', params: { template_id } });
      this.clear_search();
    },
    
    go_to_question(question_id) {
      this.$router.push({ name: 'question_management', params: { id: question_id } });
      this.clear_search();
    },
    
    go_to_user(user_id, user_role) {
      const current_user_role = this.user_current_role;

      if (current_user_role === 'Client' && this.is_workbench_client) {
        if (user_role === 'Customer') {
          this.$router.push({ 
            name: 'workbench', 
            query: { customer_id: user_id, search_trigger: 'true' } 
          });
          this.clear_search();
          return;
        }
      }
      
      if (current_user_role === 'SystemAdmin') {
        if (user_role === 'Customer') {
          this.$router.push({ name: 'view_customer', params: { user_id: user_id } });
        } else if (user_role === 'ClientAdmin') {
          this.$router.push({ name: 'view_client_admin', params: { user_id: user_id } });
        } else if (user_role === 'Client') { 
          this.$router.push({ name: 'view_client', params: { user_id: user_id } });
        } else {
          this.$router.push({ name: 'view_admin', params: { user_id: user_id } });
        }
      } else {
        if (user_role === 'Customer') {
          this.$router.push({ name: 'view_customer', params: { user_id: user_id } });
        } else { 
          this.$router.push({ name: 'client_admin', params: { id: user_id } });
        }
      }
      this.clear_search();
    },
    
    view_customer_applications(customer_id, workflow_id) {
      const current_user_role = this.user_current_role;

      if (current_user_role === 'SystemAdmin') {
        this.$router.push({ name: 'view_customer_applications', params: { id: customer_id } });
        this.clear_search();
      } else {
        this.$store.dispatch('update_workflow_id', workflow_id);
        this.$router.push({ name: 'customer_applications', params: { id: customer_id } });
        this.clear_search();
      }
    },
    
    hide_search_dropdown(event) {
      if (event && this.$refs.searchContainer?.contains(event.target)) {
        return;
      }
      this.show_results = false;
    }
  },
  
  mounted() {
    document.addEventListener('click', this.hide_search_dropdown);
    window.addEventListener('scroll', this.hide_search_dropdown);
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.hide_search_dropdown);
    window.removeEventListener('scroll', this.hide_search_dropdown);
  }
}
